import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { addUtcDays, addUtcMonths } from "@/components/tpr/RoomSchedule/Brick/BrickWeekSchedule.utils";
import { defaultButtonClasses, defaultSelectClasses } from "@/constants/ui";
import { useCalendar } from "@/contexts/calendarContext";
import useIsMobile from "@/hooks/useIsMobile";
import { getCalendarViewMaxDate } from "@/utils/bookingDateBounds";
import { localDayToUtcMidnight } from "@/lib/calendarDay";
import { cn, formatDate, formatUtcDate } from "@/lib/utils";
import { ArrowLeftIcon, ArrowRightIcon, ChevronDownIcon } from "lucide-react";
import { enGB } from "react-day-picker/locale";

type DateTumblerProps = {
  disabled?: boolean;
  className?: string;
  hideNavButtons?: boolean;
  /**
   * Optional lower bound for the calendar picker and prev-arrow navigation.
   * Defaults to 1 year ago (the existing calendar-view behaviour).
   * Pass `startOfCurrentMonth(today)` from create-booking flows to enforce
   * the current-month floor for elevated users.
   */
  pastFloor?: Date;
};

const DateTumbler = ({ disabled = false, className = "", hideNavButtons = false, pastFloor }: DateTumblerProps) => {
  const { date, setDate: handleSetDate } = useCalendar();

  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState<Date | undefined>(date || new Date());
  const [, setValue] = useState(formatDate(date || new Date()));

  // Helper to add/subtract days. Stepped in UTC (via addUtcDays) rather than local
  // `setDate(getDate() + days)` — the same class of bug fixed in BrickWeekSchedule: local stepping
  // across a viewer-local DST transition can cross a UTC-day boundary at the wrong wall-clock
  // offset, drifting or sticking the tumbler instead of advancing by exactly one calendar day.
  const addDays = (date: Date | undefined, days: number) => {
    if (!date) return new Date();
    return addUtcDays(date, days);
  };

  // Helper to add months — likewise stepped in UTC via addUtcMonths, consistent with addDays above.
  const addMonths = (date: Date, months: number) => {
    return addUtcMonths(date, months);
  };

  // Helper to compare only date part (not time)
  // const isToday = (d: Date | undefined) => {
  //   if (!d) return false;
  //   const now = new Date();
  //   return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  // };

  // Navigation constraints
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // All users can go back 1 year by default; callers may supply a tighter floor.
  const oneYearAgo = addMonths(today, -12);
  const lowerBound = pastFloor ?? oneYearAgo;

  // Calendar VIEW horizon (end of next calendar year) — deliberately wider than the bookable
  // window so members can view slot/recurring bookings that run into the following year. New
  // bookings remain capped at the role-aware window (server-side R1 + booking-flow validation),
  // so a far-future view cannot be used to book beyond the window.
  const maxForwardDate = getCalendarViewMaxDate(today);

  const canGoToPrevious = (currentDate: Date | undefined) => {
    if (!currentDate) return false;
    const checkDate = new Date(currentDate);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate > lowerBound;
  };

  const canGoToNext = (currentDate: Date | undefined) => {
    if (!currentDate) return true;
    const checkDate = new Date(currentDate);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate < maxForwardDate;
  };

  const handlePrevious = () => {
    if (!date) return;
    const newDate = addDays(date, -1);
    if (!canGoToPrevious(newDate)) {
      return;
    }
    handleSetDate(newDate);
    setMonth(newDate);
    setValue(formatDate(newDate));
  };

  const handleNext = () => {
    const currentDate = date || new Date();
    const newDate = addDays(currentDate, 1);
    if (!canGoToNext(newDate)) {
      return;
    }
    handleSetDate(newDate);
    setMonth(newDate);
    setValue(formatDate(newDate));
  };

  const shortenedDateIfMobile = useIsMobile() ? true : false;

  return (
    <div className={`flex flex-row items-center justify-between gap-2 ${className}`}>
      {!hideNavButtons && (
        <Button
          variant="outline"
          size="icon"
          aria-label="Previous date"
          id="date-previous"
          data-testid="date-tumbler-prev"
          onClick={handlePrevious}
          disabled={!canGoToPrevious(addDays(date ?? undefined, -1)) || disabled}
          className={defaultButtonClasses}
        >
          <ArrowLeftIcon />
        </Button>
      )}
      <div className="relative flex w-full min-w-0">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              id="date"
              data-testid="date-tumbler-trigger"
              className={cn(defaultSelectClasses, "shadow w-full text-base md:text-lg px-2 min-w-0")}
            >
              <span className="flex w-full items-center justify-center relative min-w-0">
                <span className="flex-1 min-w-0 text-center truncate px-5" data-testid="date-tumbler-current">
                  {/* `date` is a day-only value stored as UTC-midnight of the centre's day — render
                      it in UTC (like the grid header) so a viewer ahead of/behind UTC sees the same
                      day the grid shows, not their local roll-over of the instant. */}
                  {date ? formatUtcDate(date, false, shortenedDateIfMobile) : "Select date"}
                </span>
                <span className="absolute right-0 flex items-center">
                  <ChevronDownIcon />
                </span>
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-full overflow-hidden p-0" align="center" alignOffset={-2} sideOffset={10}>
            <Calendar
              mode="single"
              selected={date || new Date()}
              captionLayout="dropdown"
              // Bound the dropdown's selectable month/year range to the viewable window so the
              // year dropdown can reach the following year (without this, react-day-picker's
              // dropdown defaults to a narrow range that excludes next year).
              startMonth={lowerBound}
              endMonth={maxForwardDate}
              month={month}
              onMonthChange={setMonth}
              onSelect={(date) => {
                if (disabled) return;
                if (date) {
                  // react-day-picker hands back a LOCAL-midnight instant; anchor it to UTC-midnight
                  // of the clicked calendar day so the UTC-anchored grid header/fetch/URL don't read
                  // the day before (the BST off-by-one). See @/lib/calendarDay.
                  const utcDay = localDayToUtcMidnight(date);
                  handleSetDate(utcDay);
                  setValue(formatUtcDate(utcDay));
                  setOpen(false);
                }
              }}
              classNames={{
                nav: "h-4 flex flex-row justify-between gap-2",
                button_previous: "p-1",
                button_next: "p-1",
                month_grid: "min-w-[230px]",
              }}
              disabled={(checkDate) => {
                if (disabled) return true;
                if (!checkDate) return false;
                const normalized = new Date(checkDate);
                normalized.setHours(0, 0, 0, 0);
                return normalized < lowerBound || normalized > maxForwardDate;
              }}
              locale={enGB}
            />
          </PopoverContent>
        </Popover>
      </div>

      {!hideNavButtons && (
        <Button
          variant="outline"
          size="icon"
          aria-label="Next date"
          id="date-next"
          data-testid="date-tumbler-next"
          onClick={handleNext}
          disabled={!canGoToNext(addDays(date ?? undefined, 1)) || disabled}
          className={defaultButtonClasses}
        >
          <ArrowRightIcon />
        </Button>
      )}
    </div>
  );
};

export default DateTumbler;
